import { useState, useEffect, useRef } from 'react';
import { Back, Forward } from '../../assets/icons';

const DEFAULT_AUTO_INTERVAL = 4000; // Default auto slide interval in milliseconds

export const ImageCarousel = ({ gallery }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const intervalRef = useRef();
  const [modalImage, setModalImage] = useState(null);

  // Touch handling state
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const carouselRef = useRef();

  // Minimum swipe distance (în pixeli)
  const minSwipeDistance = 50;

  // Group gallery into slides of 2 images each
  const slides = [];
  for (let i = 0; i < gallery.length; i += 2) {
    slides.push(gallery.slice(i, i + 2));
  }

  useEffect(() => {
    intervalRef.current && clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setCurrentIndex((prevIndex) =>
        prevIndex === slides.length - 1 ? 0 : prevIndex + 1
      );
    }, DEFAULT_AUTO_INTERVAL);
    return () => clearInterval(intervalRef.current);
  }, [slides.length, DEFAULT_AUTO_INTERVAL]);

  const nextSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === slides.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? slides.length - 1 : prevIndex - 1
    );
  };

  const handleImageClick = (item, event) => {
    event.preventDefault();
    event.stopPropagation();
    setModalImage(item);
  };

  // Touch event handlers
  const onTouchStart = (e) => {
    setTouchEnd(null); // Reset touchEnd
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && slides.length > 1) {
      nextSlide();
    }
    if (isRightSwipe && slides.length > 1) {
      prevSlide();
    }
  };

  // Oprește auto-play când utilizatorul interacționează
  const pauseAutoPlay = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  const resumeAutoPlay = () => {
    if (slides.length > 1) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex((prevIndex) =>
          prevIndex === slides.length - 1 ? 0 : prevIndex + 1
        );
      }, DEFAULT_AUTO_INTERVAL);
    }
  };

  return (
    <div className="mx-auto rounded-lg shadow-2xl overflow-hidden">
      {/* Full screen modal */}
      {modalImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90" onClick={() => setModalImage(null)}>
          <div className="relative max-w-full max-h-full p-2">
            <img
              src={modalImage.src}
              alt={modalImage.description || ''}
              className="max-w-full max-h-full rounded-lg shadow-2xl"
              onClick={e => e.stopPropagation()}
            />
            <button
              className="
    absolute top-2 right-2
    text-white text-3xl font-bold
    bg-black bg-opacity-50
    rounded-lg w-8 h-8
    flex justify-center
    items-end
    hover:bg-opacity-80 transition"
              onClick={() => setModalImage(null)}
              aria-label="Close full screen image"
            >
              ×
            </button>
            {modalImage.description && (
              <div className="absolute bottom-2 left-2 right-2 text-white bg-black bg-opacity-50 p-2 rounded">
                <h3 className="text-lg font-bold">{modalImage.description}</h3>
              </div>
            )}
          </div>
        </div>
      )}
      <div
        className="relative h-56 overflow-hidden"
        ref={carouselRef}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onMouseEnter={pauseAutoPlay}
        onMouseLeave={resumeAutoPlay}
      >
        {/* Slides with up to 2 images each */}
        <div
          className="flex transition-transform duration-500 ease-out h-full"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        >
          {slides.map((slide, slideIdx) => (
            <div key={slideIdx} className={`min-w-full h-full flex${slide.length === 1 ? ' justify-center' : ''}`}>
              {slide.map((item, idx) => (
                <div
                  key={idx}
                  className={`relative group flex items-center justify-center ${slide.length === 1 ? 'w-full h-full' : 'w-1/2 h-full'} cursor-pointer`}
                  onClick={(e) => handleImageClick(item, e)}
                >
                  <img
                    src={item.src}
                    alt={item.description || ''}
                    className="w-full h-full object-cover pointer-events-none"
                  />
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                  {/* Description overlay */}
                  <div className="absolute bottom-4 left-4 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                    <h3 className="text-lg font-bold mb-1">{item.description}</h3>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Navigation buttons - ascunse pe mobile, vizibile pe desktop */}
        {slides.length > 1 && (
          <>
            <button
              onClick={prevSlide}
              className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/60 hover:bg-white shadow-md hover:shadow-lg rounded-full p-2.5 transition-all duration-200 hover:scale-105 group border border-gray-200 flex items-center justify-center w-10 h-10 hidden sm:flex"
              aria-label="Previous image"
            >
              <Back className="w-3 h-3 text-gray-500" />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/60 hover:bg-white shadow-md hover:shadow-lg rounded-full p-2.5 transition-all duration-200 hover:scale-105 group border border-gray-200 flex items-center justify-center w-10 h-10 hidden sm:flex"
              aria-label="Next image"
            >
              <Forward className="w-5 h-5 text-gray-500" />
            </button>
          </>
        )}

        {/* Slide indicators */}
        {slides.length > 1 && (
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
            {slides.map((_, index) => (
              <button
                key={index}
                className={`w-2 h-2 rounded-full transition-colors duration-200 ${
                  index === currentIndex
                    ? 'bg-white'
                    : 'bg-white/50 hover:bg-white/75'
                }`}
                onClick={() => setCurrentIndex(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};